/**
 * Animation helpers — pure, DOM-free utilities shared by the editor, the present-mode
 * player and the PPTX import/export paths.
 *
 * A slide stores one ordered list of animation steps:
 *
 *   slide.animations = { steps: [...], passthrough: [...] }
 *
 * `steps` is authored order — the array index IS the sequence order, so reordering is a
 * splice and there are no numbering ties to resolve. Each step targets one object by
 * (targetType, targetId). SmartArt steps additionally carry a `buildType`, which
 * `expandSteps` fans out into one resolved step per node — the same thing PowerPoint does
 * when it expands <a:bldDgm bld="one"> into individual timing nodes.
 *
 * `passthrough` holds <p:timing> fragments imported from a .pptx that we do not model
 * (emphasis, exit, motion paths). They are re-emitted verbatim on export so an
 * import -> export round-trip never destroys animation work we can't yet edit.
 */

import { v4 } from 'uuid';

/**
 * Entrance effects, in gallery order. Grouped by the visual family they belong to so the
 * gallery reads the way PowerPoint's does: subtle first, then reveals, then motion.
 *
 *   subtle   appear, fade, floatIn
 *   reveal   wipe, split, box, circle
 *   scale    zoom, stretch, growTurn
 *   motion   flyIn, bounce, swivel
 */
export const EFFECTS = [
  'appear', 'fade', 'floatIn',
  'wipe', 'split', 'box', 'circle',
  'zoom', 'stretch', 'growTurn',
  'flyIn', 'bounce', 'swivel',
];

export const EFFECT_LABELS = {
  appear: 'Appear',
  fade: 'Fade',
  floatIn: 'Float In',
  wipe: 'Wipe',
  split: 'Split',
  box: 'Box',
  circle: 'Circle',
  zoom: 'Zoom',
  stretch: 'Stretch',
  growTurn: 'Grow & Turn',
  flyIn: 'Fly In',
  bounce: 'Bounce',
  swivel: 'Swivel',
};

/**
 * Directions each effect accepts, first entry being the default. An empty list means the
 * direction control is hidden for that effect.
 */
export const EFFECT_DIRECTIONS = {
  appear: [],
  fade: [],
  zoom: [],
  bounce: [],
  growTurn: [],
  floatIn: ['fromBottom', 'fromTop'],
  flyIn: ['fromBottom', 'fromLeft', 'fromRight', 'fromTop'],
  wipe: ['fromBottom', 'fromLeft', 'fromRight', 'fromTop'],
  split: ['horizontalOut', 'horizontalIn', 'verticalOut', 'verticalIn'],
  box: ['in', 'out'],
  circle: [],
  swivel: ['horizontal', 'vertical'],
  stretch: ['fromLeft', 'fromRight', 'fromTop', 'fromBottom', 'fromCenter'],
};

export const DIRECTION_LABELS = {
  fromBottom: 'From Bottom',
  fromLeft: 'From Left',
  fromRight: 'From Right',
  fromTop: 'From Top',
  fromCenter: 'From Center',
  horizontalOut: 'Horizontal Out',
  horizontalIn: 'Horizontal In',
  verticalOut: 'Vertical Out',
  verticalIn: 'Vertical In',
  horizontal: 'Horizontal',
  vertical: 'Vertical',
  in: 'In',
  out: 'Out',
};

/** PowerPoint's three start triggers. */
export const TRIGGERS = ['onClick', 'withPrevious', 'afterPrevious'];

export const TRIGGER_LABELS = {
  onClick: 'On Click',
  withPrevious: 'With Previous',
  afterPrevious: 'After Previous',
};

/** SmartArt build types, mirroring PowerPoint's Effect Options for diagrams. */
export const BUILD_TYPES = ['asOneObject', 'allAtOnce', 'oneByOne', 'lvlOne', 'lvlAtOnce'];

export const BUILD_TYPE_LABELS = {
  asOneObject: 'As One Object',
  allAtOnce: 'All at Once',
  oneByOne: 'One by One',
  lvlOne: 'Level One by One',
  lvlAtOnce: 'Level at Once',
};

/** Object types that can carry an animation. Matches the per-slide object maps. */
export const TARGET_TYPES = ['textbox', 'image', 'shape', 'wordart', 'smartart'];

/** Which slide map holds each target type, so the player can resolve a step's object. */
export const TARGET_TYPE_COLLECTION = {
  textbox: 'textBox',
  image: 'image',
  shape: 'shapes',
  wordart: 'wordArtTextObjects',
  smartart: 'smartArtObjects',
};

export const DEFAULT_DURATION = 500; // ms
export const DEFAULT_DELAY = 0; // ms
const MAX_DURATION = 60000;
const MAX_DELAY = 60000;

export const makeAnimationId = () => `anim-${v4()}`;

/** Empty animation container, used when a slide has none yet. */
export const emptyAnimations = () => ({ steps: [], passthrough: [] });

const clampNumber = (value, fallback, max) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.min(Math.round(n), max);
};

/** Default direction for an effect, or null when the effect takes none. */
export const defaultDirection = (effect) => {
  const dirs = EFFECT_DIRECTIONS[effect];
  return dirs && dirs.length ? dirs[0] : null;
};

/**
 * Build a well-formed step, filling defaults and dropping fields that don't apply to the
 * given effect/target. Always returns a valid step so callers never hand-assemble one.
 */
export const makeStep = ({
  targetType,
  targetId,
  effect = 'fade',
  direction,
  trigger = 'onClick',
  duration = DEFAULT_DURATION,
  delay = DEFAULT_DELAY,
  buildType = 'asOneObject',
  reverse = false,
  id,
} = {}) => {
  const safeEffect = EFFECTS.includes(effect) ? effect : 'fade';
  const allowedDirs = EFFECT_DIRECTIONS[safeEffect] || [];
  const safeDirection = allowedDirs.includes(direction) ? direction : defaultDirection(safeEffect);

  const step = {
    id: id || makeAnimationId(),
    targetType: TARGET_TYPES.includes(targetType) ? targetType : 'shape',
    targetId,
    effect: safeEffect,
    trigger: TRIGGERS.includes(trigger) ? trigger : 'onClick',
    duration: clampNumber(duration, DEFAULT_DURATION, MAX_DURATION),
    delay: clampNumber(delay, DEFAULT_DELAY, MAX_DELAY),
  };
  if (safeDirection) step.direction = safeDirection;
  if (step.targetType === 'smartart') {
    step.buildType = BUILD_TYPES.includes(buildType) ? buildType : 'asOneObject';
    step.reverse = Boolean(reverse);
  }
  return step;
};

/**
 * Re-validate a step after an edit. Changing the effect can invalidate the stored
 * direction (e.g. 'fromLeft' on a zoom), so route every update through makeStep.
 */
export const normalizeStep = (step) => makeStep(step || {});

/** Normalize a whole animations container, discarding malformed steps. */
export const normalizeAnimations = (animations) => {
  const steps = Array.isArray(animations?.steps) ? animations.steps : [];
  const passthrough = Array.isArray(animations?.passthrough) ? animations.passthrough : [];
  return {
    steps: steps.filter((s) => s && s.targetId).map(normalizeStep),
    passthrough: passthrough.filter((p) => typeof p === 'string' && p.length > 0),
  };
};

/**
 * Group nodes by level for the level-based build types.
 * Returns an array of node-id arrays, ordered by first appearance of each level.
 */
const groupNodesByLevel = (nodes) => {
  const groups = [];
  const indexByLevel = new Map();
  nodes.forEach((node) => {
    const level = Number.isFinite(node.level) ? node.level : 0;
    if (!indexByLevel.has(level)) {
      indexByLevel.set(level, groups.length);
      groups.push([]);
    }
    groups[indexByLevel.get(level)].push(node.id);
  });
  return groups;
};

/**
 * Split a SmartArt step into the groups of node ids it animates, in play order.
 *
 *   asOneObject -> [[all]]        (one effect over the whole diagram)
 *   allAtOnce   -> [[all]]        (every node animates, simultaneously)
 *   oneByOne    -> [[a],[b],[c]]  (one effect per node)
 *   lvlAtOnce   -> [[l0],[l1]]    (one effect per level)
 *   lvlOne      -> [[a],[b],[c]]  (per node, ordered level by level)
 *
 * `reverse` flips group order, matching PowerPoint's Reverse Order checkbox.
 */
export const buildGroups = (buildType, nodes = [], reverse = false) => {
  const ids = nodes.map((n) => n.id).filter(Boolean);
  if (!ids.length) return [];

  let groups;
  switch (buildType) {
    case 'oneByOne':
      groups = ids.map((id) => [id]);
      break;
    case 'lvlAtOnce':
      groups = groupNodesByLevel(nodes);
      break;
    case 'lvlOne':
      groups = groupNodesByLevel(nodes).flatMap((group) => group.map((id) => [id]));
      break;
    case 'allAtOnce':
    case 'asOneObject':
    default:
      groups = [ids];
      break;
  }
  return reverse ? groups.slice().reverse() : groups;
};

/**
 * Expand one authored step into the resolved steps the player and exporter consume.
 *
 * Non-SmartArt targets expand to a single resolved step. SmartArt fans out per
 * `buildType`, each resolved step carrying the node ids it covers in `subTargetIds`.
 * Only the first resolved step keeps the authored trigger; the rest follow with
 * 'afterPrevious' so a single build plays as one continuous sequence — the same
 * expansion PowerPoint performs when it saves a diagram build.
 *
 * `asOneObject` is deliberately distinct from `allAtOnce`: the former animates the
 * container as a single unit (subTargetIds null), the latter animates every node
 * together but individually, which matters for per-node transforms like Fly In.
 */
export const expandSteps = (step, targetObject) => {
  if (!step) return [];
  if (step.targetType !== 'smartart') return [{ ...step, subTargetIds: null }];

  const nodes = Array.isArray(targetObject?.nodes) ? targetObject.nodes : [];
  if (step.buildType === 'asOneObject' || !nodes.length) {
    return [{ ...step, subTargetIds: null }];
  }

  const groups = buildGroups(step.buildType, nodes, step.reverse);
  if (!groups.length) return [{ ...step, subTargetIds: null }];

  return groups.map((ids, index) => ({
    ...step,
    id: `${step.id}::${index}`,
    parentId: step.id,
    subTargetIds: ids,
    trigger: index === 0 ? step.trigger : 'afterPrevious',
    // Only the authored delay applies to the first piece; the rest chain immediately.
    delay: index === 0 ? step.delay : 0,
  }));
};

/**
 * Expand every step on a slide, resolving each target through the slide's object maps.
 * Steps whose target no longer exists are dropped (the object was deleted).
 */
export const expandSlideSteps = (animations, slideData) => {
  const { steps } = normalizeAnimations(animations);
  return steps.flatMap((step) => {
    const collection = TARGET_TYPE_COLLECTION[step.targetType];
    const target = collection ? slideData?.[collection]?.[step.targetId] : null;
    if (!target) return [];
    return expandSteps(step, target);
  });
};

/**
 * Partition resolved steps into click-advanced groups.
 *
 * A group starts at each 'onClick' step and absorbs every following 'withPrevious' /
 * 'afterPrevious' step. Group 0 is what plays when the slide first appears — it exists
 * only if the slide opens with non-onClick steps, otherwise the slide starts with
 * everything hidden and the first click plays group 1.
 *
 * Returns an array of groups; advancing the slide plays groups in order.
 */
export const groupIntoClickSteps = (resolvedSteps = []) => {
  const groups = [];
  let current = null;
  resolvedSteps.forEach((step) => {
    if (step.trigger === 'onClick' || !current) {
      current = [];
      groups.push(current);
    }
    current.push(step);
  });
  return groups;
};

/**
 * Assign each resolved step an absolute start offset (ms) within its click group.
 * 'withPrevious' starts alongside the previous step; 'afterPrevious' starts when the
 * previous step finishes. The first step in a group starts at its own delay.
 */
export const scheduleGroup = (group = []) => {
  let previousStart = 0;
  let previousEnd = 0;
  return group.map((step, index) => {
    let start;
    if (index === 0) start = step.delay;
    else if (step.trigger === 'withPrevious') start = previousStart + step.delay;
    else start = previousEnd + step.delay;

    previousStart = start;
    previousEnd = start + step.duration;
    return { ...step, startOffset: start };
  });
};

/** Total wall-clock duration (ms) of a scheduled group. */
export const groupDuration = (scheduledGroup = []) =>
  scheduledGroup.reduce((max, s) => Math.max(max, s.startOffset + s.duration), 0);

/**
 * Full playback timeline for a slide: click groups, each scheduled and ready to play.
 *   [{ steps: [{...step, startOffset}], duration }]
 */
export const buildTimeline = (animations, slideData) => {
  const resolved = expandSlideSteps(animations, slideData);
  return groupIntoClickSteps(resolved).map((group) => {
    const steps = scheduleGroup(group);
    return { steps, duration: groupDuration(steps) };
  });
};

/**
 * Every (targetId, subTargetId) pair the timeline animates, as a Set of keys. Objects in
 * this set start hidden in present mode; everything else is visible from the start.
 * A null subTarget is keyed by target id alone.
 */
export const animatedKey = (targetId, subTargetId = null) =>
  subTargetId ? `${targetId}::${subTargetId}` : targetId;

export const collectAnimatedKeys = (timeline = []) => {
  const keys = new Set();
  timeline.forEach((group) =>
    group.steps.forEach((step) => {
      if (step.subTargetIds && step.subTargetIds.length) {
        step.subTargetIds.forEach((sub) => keys.add(animatedKey(step.targetId, sub)));
      } else {
        keys.add(animatedKey(step.targetId));
      }
    })
  );
  return keys;
};

/** Find the authored step targeting an object, or undefined. */
export const findStepForTarget = (animations, targetId) =>
  (animations?.steps || []).find((s) => s.targetId === targetId);

/** 1-based sequence badge for an object, or null when it has no animation. */
export const sequenceNumberForTarget = (animations, targetId) => {
  const index = (animations?.steps || []).findIndex((s) => s.targetId === targetId);
  return index === -1 ? null : index + 1;
};
